// ── Statisztika-oldal chartjai (Recharts) ──────────────────────────────────
// Részesedés-oszlopok (Kategóriák/Partnerek), havi kategória-trend (stacked),
// tartozás-öregedés (aging) és projekt P&L chartok. A közös vizuális elemeket
// (tengely-tipó, grid, legend, pénz-tooltip) a ChartFrame adja – egy rendszer.
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, Cell, LabelList, ReferenceLine,
} from 'recharts';
import { huFmt } from '../../utils';
import { MoneyTooltip, axisTick, gridProps, legendProps, barCursor } from './ChartFrame';

// Kompakt HUF: 4 200 000 → "4,2 M" · 820 000 → "820 E" · 950 → "950"
export const fmtM = (v) => {
  const a = Math.abs(Number(v) || 0);
  if (a >= 1e9) return `${huFmt(v / 1e9, 1)} Mrd`;
  if (a >= 1e6) return `${huFmt(v / 1e6, 1)} M`;
  if (a >= 1e3) return `${huFmt(v / 1e3, 0)} E`;
  return huFmt(v, 0);
};

// Hosszú entitás-név rövidítése a kategória-tengelyre (tooltipben a teljes név).
export const truncName = (s, n = 22) =>
  (s || '').length > n ? `${(s || '').slice(0, n - 1)}…` : (s || '');

const HU_MONTHS_LONG = ['január', 'február', 'március', 'április', 'május', 'június',
                        'július', 'augusztus', 'szeptember', 'október', 'november', 'december'];
const longMonth = (m) => {
  const i = parseInt(String(m).slice(5, 7), 10) - 1;
  return HU_MONTHS_LONG[i] ? `${String(m).slice(0, 4)}. ${HU_MONTHS_LONG[i]}` : m;
};

// Kategória-tengely tick: ez NÉV (nem szám) → nem mono, alap szövegszín.
const nameTick = { fontSize: 12.5, fill: 'var(--text)' };

// Tooltip-doboz (a ChartFrame MoneyTooltip-jával egyező stílus).
const tipBox = {
  background: 'var(--bg-surface)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius)', padding: '0.55rem 0.75rem',
  boxShadow: 'var(--shadow)', fontSize: 'var(--fs-xs)', minWidth: 160,
};
const tipMono = {
  marginLeft: 'auto', fontFamily: 'var(--mono)', color: 'var(--text)',
  fontVariantNumeric: 'tabular-nums', paddingLeft: 12,
};

// A 100%-os sáv (track) háttere – a részesedés-oszlopok skálája a TELJES összeg.
export const SHARE_TRACK = 'rgba(139,155,180,0.09)';

// ── Részesedés-oszlopok (share bars) ───────────────────────────────────────
// data: [{ name, fullName?, value, count?, color }] – érték szerint csökkenő.
// A sáv hossza = részesedés a TELJES összegből (XAxis domain [0, total]).

function ShareLabel({ x, y, width, height, index, data, total }) {
  const d = data[index];
  if (!d || !total || !(d.value > 0)) return null;
  const pct = Math.round((d.value / total) * 100);
  const text = `${fmtM(d.value)} · ${pct < 1 ? '<1' : pct}%`;
  const trackRight = x + (width * total) / d.value;      // a 100%-os sáv jobb széle
  const est = text.length * 6.6 + 12;                    // becsült szöveg-szélesség
  const outside = x + width + est <= trackRight;         // kifér-e a sáv végén kívülre
  return (
    <text
      x={outside ? x + width + 8 : Math.max(x + width - 8, x + est)}
      y={y + height / 2 + 3.5}
      textAnchor={outside ? 'start' : 'end'}
      fontSize={11.5} fontFamily="var(--mono)"
      style={{ fontVariantNumeric: 'tabular-nums' }}
      fill={outside ? 'var(--text-muted)' : '#fff'}
    >
      {text}
    </text>
  );
}

function ShareTip({ active, payload, total }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  const pct = total ? ((p.value / total) * 100).toFixed(1).replace('.', ',') : '0';
  return (
    <div style={tipBox}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
        <span style={{ width: 8, height: 8, borderRadius: 2, flexShrink: 0, background: p.color }} />
        <span style={{ color: 'var(--text)', fontWeight: 600 }}>{p.fullName || p.name}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 3 }}>
        <span style={{ color: 'var(--text)' }}>Összeg</span>
        <span style={tipMono}>{huFmt(p.value)} Ft</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: p.count != null ? 3 : 0 }}>
        <span style={{ color: 'var(--text-muted)' }}>Részesedés</span>
        <span style={{ ...tipMono, color: 'var(--text-muted)' }}>{pct}%</span>
      </div>
      {p.count != null && (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ color: 'var(--text-muted)' }}>Tételek</span>
          <span style={{ ...tipMono, color: 'var(--text-muted)' }}>{p.count} db</span>
        </div>
      )}
    </div>
  );
}

export function ShareBarChart({ data = [] }) {
  const total = data.reduce((s, d) => s + (d.value || 0), 0);
  if (!data.length || total <= 0) {
    return <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>Nincs adat.</p>;
  }
  const h = data.length * 30 + 16;
  return (
    <ResponsiveContainer width="100%" height={h}>
      <BarChart layout="vertical" data={data}
        margin={{ top: 4, right: 8, left: 0, bottom: 4 }} barCategoryGap="24%">
        <XAxis type="number" domain={[0, total]} hide />
        <YAxis type="category" dataKey="name" width={140} tick={nameTick}
          tickLine={false} axisLine={false} interval={0} />
        <Tooltip cursor={barCursor} content={<ShareTip total={total} />} />
        <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={18}
          background={{ fill: SHARE_TRACK, radius: 4 }} isAnimationActive={false}>
          {data.map((d) => <Cell key={d.fullName || d.name} fill={d.color} />)}
          <LabelList dataKey="value"
            content={(p) => <ShareLabel {...p} data={data} total={total} />} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Havi kategória-trend (stacked, utolsó 12 hónap) ────────────────────────
// data: [{ month:'2025-08', c0:…, c1:…, other:… }] – kulcsok szanitizálva
// (a kategórianévben pont lehet, ami a Recharts dataKey-ben útvonalnak számítana).
// series: [{ key, name, color }] – FIX sorrend (összeg szerint), sosem újraciklizva.
export function CategoryTrendChart({ data = [], series = [], height = 260 }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} barCategoryGap="30%"
        margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid {...gridProps} />
        <XAxis dataKey="month" tickFormatter={(m) => String(m).slice(2)} tick={axisTick}
          axisLine={{ stroke: 'var(--border)' }} tickLine={false} interval={0} />
        <YAxis tickFormatter={fmtM} tick={axisTick} axisLine={false} tickLine={false} width={52} />
        <Tooltip cursor={barCursor} content={<MoneyTooltip labelFormatter={longMonth} />} />
        <Legend {...legendProps} />
        {series.map((s, i) => (
          <Bar key={s.key} dataKey={s.key} name={s.name} stackId="cat" fill={s.color}
            maxBarSize={26} isAnimationActive={false}
            radius={i === series.length - 1 ? [3, 3, 0, 0] : 0} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Éves kategória-összehasonlító ───────────────────────────────────────────
// data: [{ year:'2024', c0:…, c1:…, other:… }] – ugyanaz a kulcs-alak, mint a
// havi trendnél, csak évekre. NEM ugyanaz a komponens: a hónap-tengely
// `.slice(2)`-es rövidítése és a `longMonth` tooltip-formázó egy sima "2024"
// évszámra hibás/felesleges lenne.
export function CategoryYearlyChart({ data = [], series = [], height = 260 }) {
  if (!data.length) {
    return <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>Nincs korábbi évekre adat.</p>;
  }
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} barCategoryGap="30%"
        margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid {...gridProps} />
        <XAxis dataKey="year" tick={axisTick}
          axisLine={{ stroke: 'var(--border)' }} tickLine={false} interval={0} />
        <YAxis tickFormatter={fmtM} tick={axisTick} axisLine={false} tickLine={false} width={52} />
        <Tooltip cursor={barCursor} content={<MoneyTooltip />} />
        <Legend {...legendProps} />
        {series.map((s, i) => (
          <Bar key={s.key} dataKey={s.key} name={s.name} stackId="cat" fill={s.color}
            maxBarSize={40} isAnimationActive={false}
            radius={i === series.length - 1 ? [3, 3, 0, 0] : 0} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Tartozás-öregedés (aging) ──────────────────────────────────────────────
// Kockázati skála: még nem esedékes = visszafogott szürkéskék, majd egyre
// mélyülő piros a 90+ napig. A tile-sor és a stacked chart is ebből dolgozik.
export const AGING_BUCKETS = [
  { key: 'not_due',  label: 'Még nem esedékes', text: 'var(--text-muted)', fill: 'rgba(139,155,180,0.35)' },
  { key: 'd0_30',    label: '1–30 nap',          text: '#f59e0b',           fill: '#f59e0b' },
  { key: 'd31_60',   label: '31–60 nap',         text: '#ea580c',           fill: '#ea580c' },
  { key: 'd61_90',   label: '61–90 nap',         text: '#dc2626',           fill: '#dc2626' },
  { key: 'd90_plus', label: '90+ nap',           text: '#991b1b',           fill: '#991b1b' },
];

// data: [{ name, fullName, not_due, d0_30, d31_60, d61_90, d90_plus }]
export function AgingBarChart({ data = [] }) {
  if (!data.length) return null;
  const h = data.length * 34 + 46;   // +legend
  const nameOf = (label) => data.find((d) => d.name === label)?.fullName || label;
  // Szegmens-sorrend: a lejárt sávok az alapvonaltól, a „még nem esedékes" a végén.
  const order = [...AGING_BUCKETS.slice(1), AGING_BUCKETS[0]];
  return (
    <ResponsiveContainer width="100%" height={h}>
      <BarChart layout="vertical" data={data}
        margin={{ top: 4, right: 16, left: 0, bottom: 0 }} barCategoryGap="28%">
        <CartesianGrid {...gridProps} vertical={true} horizontal={false} />
        <XAxis type="number" tickFormatter={fmtM} tick={axisTick}
          axisLine={{ stroke: 'var(--border)' }} tickLine={false} />
        <YAxis type="category" dataKey="name" width={140} tick={nameTick}
          tickLine={false} axisLine={false} interval={0} />
        <Tooltip cursor={barCursor} content={<MoneyTooltip labelFormatter={nameOf} />} />
        <Legend {...legendProps} />
        {order.map((b) => (
          <Bar key={b.key} dataKey={b.key} name={b.label} stackId="age" fill={b.fill}
            maxBarSize={18} isAnimationActive={false} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Projekt P&L: bevétel vs költség (grouped, vízszintes) ──────────────────
// data: [{ name, fullName, revenue, cost }] – bevétel szerint csökkenő.
export function PnlGroupedChart({ data = [] }) {
  if (!data.length) return null;
  const h = data.length * 46 + 56;   // 2 sáv/projekt + legend
  const nameOf = (label) => data.find((d) => d.name === label)?.fullName || label;
  return (
    <ResponsiveContainer width="100%" height={h}>
      <BarChart layout="vertical" data={data} barGap={2}
        margin={{ top: 4, right: 16, left: 0, bottom: 0 }} barCategoryGap="26%">
        <CartesianGrid {...gridProps} vertical={true} horizontal={false} />
        <XAxis type="number" tickFormatter={fmtM} tick={axisTick}
          axisLine={{ stroke: 'var(--border)' }} tickLine={false} />
        <YAxis type="category" dataKey="name" width={120} tick={nameTick}
          tickLine={false} axisLine={false} interval={0} />
        <Tooltip cursor={barCursor} content={<MoneyTooltip labelFormatter={nameOf} />} />
        <Legend {...legendProps} />
        <Bar dataKey="revenue" name="Bevétel" fill="var(--chart-in)"
          radius={[0, 4, 4, 0]} maxBarSize={14} isAnimationActive={false} />
        <Bar dataKey="cost" name="Költség" fill="var(--chart-out)"
          radius={[0, 4, 4, 0]} maxBarSize={14} isAnimationActive={false} />
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Projekt P&L: eredmény nulla körül (diverging) ──────────────────────────
function ProfitLabel({ x, y, width, height, value }) {
  if (value == null) return null;
  const neg = value < 0;
  const text = `${neg ? '−' : '+'}${fmtM(Math.abs(value))}`;
  // Negatív sávnál a Recharts adhat negatív width-et is → explicit bal/jobb vég.
  const barLeft = Math.min(x, x + width);
  const barRight = Math.max(x, x + width);
  return (
    <text
      x={neg ? barLeft - 6 : barRight + 6}
      y={y + height / 2 + 3.5}
      textAnchor={neg ? 'end' : 'start'}
      fontSize={11.5} fontFamily="var(--mono)"
      style={{ fontVariantNumeric: 'tabular-nums' }}
      fill="var(--text-muted)"
    >
      {text}
    </text>
  );
}

function ProfitTip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  const neg = p.profit < 0;
  return (
    <div style={tipBox}>
      <div style={{ color: 'var(--text-muted)', fontWeight: 600, marginBottom: 5 }}>
        {p.fullName || p.name}
      </div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span style={{ color: 'var(--text)' }}>Eredmény</span>
        <span style={{
          ...tipMono, fontWeight: 600,
          color: neg ? 'var(--danger)' : 'var(--chart-in)',
        }}>
          {neg ? '−' : '+'}{huFmt(Math.abs(p.profit))} Ft
        </span>
      </div>
    </div>
  );
}

// data: [{ name, fullName, profit }] – eredmény szerint csökkenő.
export function PnlProfitChart({ data = [] }) {
  if (!data.length) return null;
  const h = data.length * 34 + 24;
  return (
    <ResponsiveContainer width="100%" height={h}>
      <BarChart layout="vertical" data={data}
        margin={{ top: 4, right: 16, left: 0, bottom: 0 }} barCategoryGap="28%">
        <CartesianGrid {...gridProps} vertical={true} horizontal={false} />
        {/* domain-párnázás, hogy a sáv-végi címkék ne lógjanak ki */}
        <XAxis type="number" tickFormatter={fmtM} tick={axisTick}
          axisLine={{ stroke: 'var(--border)' }} tickLine={false}
          domain={[(min) => (min < 0 ? min * 1.35 : 0), (max) => (max > 0 ? max * 1.2 : 0)]} />
        <YAxis type="category" dataKey="name" width={120} tick={nameTick}
          tickLine={false} axisLine={false} interval={0} />
        <Tooltip cursor={barCursor} content={<ProfitTip />} />
        <ReferenceLine x={0} stroke="var(--text-muted)" strokeDasharray="4 3" />
        <Bar dataKey="profit" name="Eredmény" radius={[0, 4, 4, 0]} maxBarSize={16}
          isAnimationActive={false}>
          {data.map((d) => (
            <Cell key={d.fullName || d.name}
              fill={d.profit >= 0 ? 'var(--chart-in)' : 'var(--danger)'} />
          ))}
          <LabelList dataKey="profit" content={<ProfitLabel />} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
